/**
 * AI CONTEXT
 * Purpose: user identity/session context (pin login, current user, role override, impersonation, user settings).
 * Key outputs: currentUser/effectiveUser, effectiveRole, settings helpers.
 * Side effects: impersonation analytics, logout cleanup, activity timeout.
 * Used by: most route guards, permissions, and role-dependent views.
 * Refactor: separate impersonation and role-override logic into dedicated hooks/modules.
 */
import { noop } from "lodash"
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import Firebase from "#classes/Firebase"
import { ROUTES } from "#constants/routes"
import { useAuthorizationContext } from "#contexts/AuthorizationContextProvider"
import { USER_ROLES } from "#customTypes/composedTypes/authTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { UserSettings, Uuid } from "#customTypes/globalTypes"
import { EnumValues, TypesafeAny } from "#customTypes/types"
import useActivityMonitor from "#hooks/newHooks/useActivityMonitor"
import { useGetOneUser, useGetUserByPinHash } from "#hooks/newHooks/useGetUser"
import useUserSettings from "#hooks/newHooks/useUserSettings"
import useAnalytics from "#hooks/useAnalytics"
import useErrorModal from "#hooks/useErrorModal"
import { hashPin } from "#utils/authHelpers"
import { canAssignRole, ROLE_HIERARCHY } from "#utils/permissionsHelpers"

const DEBUG_MODE: boolean = import.meta.env.VITE_DEBUG_MODE === "true"
const DEBUG_USER_ID: Uuid | null = import.meta.env.VITE_DEBUG_USER_ID

const IMPERSONATION_STORAGE_KEY = "jarvis-impersonated-user-id"
const IMPERSONATION_START_TIME_KEY = "jarvis-impersonation-start-time"

interface UserContextType {
  currentUserId: Uuid | null
  setCurrentUserId: (userId: Uuid | null) => void
  currentUser: UserConstructor | null

  // Impersonation
  impersonatedUserId: Uuid | null
  effectiveUserId: Uuid | null
  effectiveUser: UserConstructor | null
  effectiveRole: EnumValues<typeof USER_ROLES>
  startImpersonation: (targetUser: UserConstructor) => void
  stopImpersonation: () => void
  canImpersonate: (targetUser: UserConstructor) => boolean

  // Role Override
  isRoleOverrideActive: boolean
  startRoleOverride: (role: EnumValues<typeof USER_ROLES>) => void
  stopRoleOverride: () => void

  settings: {
    showIds: boolean
  }
  setUserSetting: (key: keyof UserSettings, value: TypesafeAny) => void
  getUserSetting: <T extends TypesafeAny>(path: keyof UserSettings) => T
  setBulkUserSettings: (settings: Partial<UserSettings>) => void

  isPinLoggedIn: boolean
  onPinLogin: (pin: string) => Promise<string>
  onPinLogout: () => void

  role: EnumValues<typeof USER_ROLES>
}

export const UserContext = createContext<UserContextType>({
  currentUserId: null,
  setCurrentUserId: noop,
  currentUser: null,

  // Impersonation
  impersonatedUserId: null,
  effectiveUserId: null,
  effectiveUser: null,
  effectiveRole: USER_ROLES.DISABLED,
  startImpersonation: noop,
  stopImpersonation: noop,
  canImpersonate: () => false,

  // Role Override
  isRoleOverrideActive: false,
  startRoleOverride: noop,
  stopRoleOverride: noop,

  settings: { showIds: false },
  setUserSetting: noop,
  getUserSetting: <T extends TypesafeAny>(): T => undefined as T,
  setBulkUserSettings: noop,

  isPinLoggedIn: false,
  onPinLogin: async () => ROUTES.FRONT_DESK_VIEW,
  onPinLogout: noop,

  role: USER_ROLES.DISABLED,
})

const getStoredImpersonationId = (): Uuid | null => {
  try {
    return localStorage.getItem(IMPERSONATION_STORAGE_KEY) as Uuid | null
  } catch {
    return null
  }
}

const getStoredImpersonationStartTime = (): number | null => {
  try {
    const stored = localStorage.getItem(IMPERSONATION_START_TIME_KEY)
    return stored ? parseInt(stored, 10) : null
  } catch {
    return null
  }
}

const clearImpersonationStorage = () => {
  try {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
    localStorage.removeItem(IMPERSONATION_START_TIME_KEY)
  } catch {
    // Ignore localStorage errors
  }
}

const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const usersByPinHash = useGetUserByPinHash()
  const {
    trackUserPinMissing,
    trackImpersonationStarted,
    trackImpersonationStopped,
  } = useAnalytics()
  const openErrorModal = useErrorModal("User PIN is missing")

  const [isPinLoggedIn, setIsPinLoggedIn] = useState(DEBUG_MODE)
  const [currentUserId, setCurrentUserId] = useState<Uuid | null>(
    DEBUG_MODE ? DEBUG_USER_ID : null
  )

  // Impersonation state - restore from localStorage on mount
  const [impersonatedUserId, setImpersonatedUserId] = useState<Uuid | null>(
    getStoredImpersonationId
  )
  // Track start time for duration calculation - persisted in localStorage
  const impersonationStartTimeRef = useRef<number | null>(
    getStoredImpersonationStartTime()
  )

  useEffect(() => {
    if (DEBUG_MODE) {
      setCurrentUserId(DEBUG_USER_ID)
    }
  }, [])

  // Set the current user ID on the Firebase instance
  useEffect(() => {
    Firebase.getInstance().setCurrentUserId(currentUserId)
  }, [currentUserId])

  const currentUser = useGetOneUser(currentUserId)
  const impersonatedUser = useGetOneUser(impersonatedUserId)

  // Log if the user's pin goes missing
  useEffect(() => {
    if (currentUser && !currentUser.pinHash) {
      trackUserPinMissing("User pin missing after login")
      openErrorModal(
        new Error("User pin missing while logged in and using the app"),
        `Current user: ${currentUser.name} (${currentUser.id})
Current page: ${location.pathname}`
      )
    }
  }, [currentUser, trackUserPinMissing, openErrorModal])

  const { setUserSetting, getUserSetting, setBulkUserSettings } =
    useUserSettings(currentUser)

  const [roleOverride, setRoleOverride] = useState<EnumValues<
    typeof USER_ROLES
  > | null>(null)
  const isRoleOverrideActive = roleOverride !== null
  const currentUserRole = useMemo(() => {
    return roleOverride ?? currentUser?.role ?? USER_ROLES.DISABLED
  }, [currentUser, roleOverride])

  // Role override helpers
  const startRoleOverride = useCallback(
    (role: EnumValues<typeof USER_ROLES>) => {
      // Cannot start role override while impersonating
      if (impersonatedUserId) return

      // Enforce override ceiling using the real user role.
      const realUserRole = currentUser?.role ?? USER_ROLES.DISABLED
      if (!canAssignRole(realUserRole, role)) {
        console.warn(`Cannot override to higher role: ${role}`)
        return
      }

      setRoleOverride(role)
    },
    [currentUser, impersonatedUserId]
  )

  const stopRoleOverride = useCallback(() => {
    setRoleOverride(null)
  }, [])

  // Impersonation helpers
  const canImpersonate = useCallback(
    (targetUser: UserConstructor): boolean => {
      if (!currentUser) return false
      // Cannot impersonate while already impersonating
      if (impersonatedUserId) return false
      // Cannot impersonate while role override is active
      if (isRoleOverrideActive) return false
      // Cannot impersonate self
      if (targetUser.id === currentUserId) return false

      // Use the REAL user role for hierarchy check, not overridden role
      const realUserRole = currentUser.role ?? USER_ROLES.DISABLED
      const currentUserLevel = ROLE_HIERARCHY[realUserRole]
      const targetLevel = ROLE_HIERARCHY[targetUser.role ?? USER_ROLES.DISABLED]

      // Can only impersonate users with LOWER hierarchy level
      return currentUserLevel > targetLevel
    },
    [currentUser, currentUserId, impersonatedUserId, isRoleOverrideActive]
  )

  const startImpersonation = useCallback(
    (targetUser: UserConstructor) => {
      if (!currentUser || impersonatedUserId) return

      // Validate that current user can impersonate target user
      // Use the REAL user role for hierarchy check, not overridden role
      const realUserRole = currentUser.role ?? USER_ROLES.DISABLED
      const currentUserLevel = ROLE_HIERARCHY[realUserRole]
      const targetLevel = ROLE_HIERARCHY[targetUser.role ?? USER_ROLES.DISABLED]

      // Can only impersonate users with LOWER hierarchy level
      if (currentUserLevel <= targetLevel) {
        console.warn(
          `Cannot impersonate user with equal or higher role: ${targetUser.role}`
        )
        return
      }

      // Cannot impersonate self
      if (targetUser.id === currentUserId) {
        console.warn("Cannot impersonate self")
        return
      }

      const startTime = Date.now()
      impersonationStartTimeRef.current = startTime
      setImpersonatedUserId(targetUser.id)
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, targetUser.id)
      localStorage.setItem(IMPERSONATION_START_TIME_KEY, startTime.toString())
      trackImpersonationStarted(currentUserId, targetUser.id)
    },
    [currentUser, currentUserId, impersonatedUserId, trackImpersonationStarted]
  )

  const stopImpersonation = useCallback(() => {
    if (!impersonatedUserId) return

    const duration = impersonationStartTimeRef.current
      ? Date.now() - impersonationStartTimeRef.current
      : 0
    trackImpersonationStopped(currentUserId, impersonatedUserId, duration)

    impersonationStartTimeRef.current = null
    setImpersonatedUserId(null)
    clearImpersonationStorage()
  }, [currentUserId, impersonatedUserId, trackImpersonationStopped])

  // Effective user/role for views (considers impersonation)
  const effectiveUserId = impersonatedUserId ?? currentUserId
  const effectiveUser = impersonatedUser ?? currentUser
  const effectiveRole = useMemo(() => {
    if (impersonatedUser) {
      return impersonatedUser.role ?? USER_ROLES.DISABLED
    }
    return currentUserRole
  }, [impersonatedUser, currentUserRole])

  const onPinLogin = useCallback(
    async (pin: string): Promise<string> => {
      const pinHash = await hashPin(pin)
      const user = usersByPinHash[pinHash]

      if (user?.role === USER_ROLES.DISABLED) {
        throw new Error("This account has been disabled")
      }

      if (pin.length !== 4) {
        throw new Error("PIN should be 4 digits")
      }

      if (user?.id != null) {
        setIsPinLoggedIn(true)
        setCurrentUserId(user.id)
      } else {
        trackUserPinMissing("Invalid pin entered")
        throw new Error("Invalid pin")
      }

      const isTeacher =
        user?.role === USER_ROLES.TEACHER ||
        user?.role === USER_ROLES.LEAD_TEACHER
      return (
        user?.settings?.lastPage ||
        (isTeacher ? ROUTES.TEACHER_VIEW : ROUTES.FRONT_DESK_VIEW)
      )
    },
    [usersByPinHash, trackUserPinMissing]
  )

  const onPinLogout = useCallback(() => {
    // Clear impersonation and role override on logout
    if (impersonatedUserId) {
      impersonationStartTimeRef.current = null
      setImpersonatedUserId(null)
      clearImpersonationStorage()
    }
    if (roleOverride) {
      setRoleOverride(null)
    }
    setIsPinLoggedIn(false)
    setCurrentUserId(null)
  }, [impersonatedUserId, roleOverride])

  // Clear impersonation when manager logs out (Firebase auth logout)
  const wasManagerLoggedIn = useRef(isManagerLoggedIn)
  useEffect(() => {
    // Detect manager logout (transition from logged in to logged out)
    if (wasManagerLoggedIn.current && !isManagerLoggedIn) {
      // Clear impersonation state without tracking (session ended)
      impersonationStartTimeRef.current = null
      setImpersonatedUserId(null)
      clearImpersonationStorage()
      setRoleOverride(null)
      setIsPinLoggedIn(false)
      setCurrentUserId(null)
    }
    wasManagerLoggedIn.current = isManagerLoggedIn
  }, [isManagerLoggedIn])

  useActivityMonitor(isPinLoggedIn, onPinLogout)

  const defaultValue = {
    currentUserId,
    setCurrentUserId,
    currentUser,

    // Impersonation
    impersonatedUserId,
    effectiveUserId,
    effectiveUser,
    effectiveRole,
    startImpersonation,
    stopImpersonation,
    canImpersonate,

    // Role Override
    isRoleOverrideActive,
    startRoleOverride,
    stopRoleOverride,

    settings: {
      showIds: true,
    },
    setUserSetting,
    getUserSetting,
    setBulkUserSettings,

    isPinLoggedIn,
    onPinLogin,
    onPinLogout,

    role: currentUserRole,
  }

  return (
    <UserContext.Provider value={defaultValue}>{children}</UserContext.Provider>
  )
}

export const useUserContext = () => useContext(UserContext)

export default UserContextProvider
